import React, { useEffect, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import axios from 'axios';
import { API_BASE_URL } from '../infra/apiConfig';

// =================================================================
// FUNÇÕES DE FORMATAÇÃO DE MÁSCARA
// =================================================================
const formatCPF = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .substring(0, 14);
};

const formatTelefone = (value) => {
  if (!value) return "";
  return value.replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .substring(0, 15);
};

// =================================================================
// Componente Input refatorado para ser mais simples
// =================================================================
const Input = ({ label, error, ...props }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      {...props}
      className={`mt-1 w-full p-2 border ${props.disabled ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'
        } ${error ? 'border-red-500' : 'border-gray-300'} rounded-md`}
    />
    {error && <span className="text-red-600 text-sm mt-1">{error.message}</span>}
  </div>
);

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const Step1_InfoEquipe = ({ control, register: formRegister, errors, reset, getValues, setValue, watch }) => {
  const [lider, setLider] = useState(null);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "novosMembros",
    rules: {
      minLength: {
        value: 1,
        message: "A equipe precisa ter pelo menos mais um integrante além do líder."
      }
    }
  });

  // Helpers para detectar categoria
  const isPassaporteCategory = (cat) => {
    if (!cat) return false;
    const c = cat.toString().toUpperCase();
    return c.includes('PASSAPORTE');
  };
  const isExternoCategory = (cat) => {
    if (!cat) return false;
    const c = cat.toString().toUpperCase();
    // opção do select: "ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA"
    return c.includes('REDE DE ESCOLAS') || c.includes('ESTUDANTE DA REDE') || c.includes('EXTERNO');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resLider = await axios.get(`${API_BASE_URL}/perfil`, getAuthHeaders());
        setLider(resLider.data || null);

        // registra/atualiza campo oculto no form com a categoria do líder (para validação no parent)
        if (setValue) {
          setValue('liderCategoria', resLider.data?.categoria || '');
        }
      } catch (error) {
        console.error("Erro ao buscar dados do líder:", error);
      }
    };
    fetchData();
  }, [setValue]);

  useEffect(() => {
    const fetchEquipe = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/minha-equipe`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.equipe) {
          const membrosFormatados = (res.data.membros || []).map(membro => ({
            ...membro,
            data_nascimento: membro.data_nascimento
              ? membro.data_nascimento.split('T')[0]
              : ''
          }));

          reset({
            nomeEquipe: res.data.equipe.nome_equipe,
            novosMembros: membrosFormatados
          });
        }
      } catch (err) {
        console.error("Erro ao buscar equipe:", err);
      }
    };

    fetchEquipe();
  }, [reset]);

  const handleInputChange = (event, formatFunction) => {
    event.target.value = formatFunction(event.target.value);
  };

  const leaderIsPass = isPassaporteCategory(lider?.categoria);
  const leaderIsExt = isExternoCategory(lider?.categoria);

  return (
    <div className="space-y-6">
      {/* Register hidden field so parent can read liderCategoria via getValues() */}
      <input type="hidden" {...formRegister('liderCategoria')} />

      <h2 className="text-xl font-semibold text-gray-800">1. Informações da Equipe</h2>

      <div>
        <label htmlFor="nomeEquipe" className="block text-sm font-medium text-gray-700">
          Nome da Equipe
        </label>
        <input
          id="nomeEquipe"
          {...formRegister("nomeEquipe", { required: "O nome da equipe é obrigatório." })}
          className={`mt-1 w-full p-2 bg-white border ${errors.nomeEquipe ? 'border-red-500' : 'border-gray-300'
            } rounded-md`}
        />
        {errors.nomeEquipe && (
          <span className="text-red-600 text-sm">{errors.nomeEquipe.message}</span>
        )}
      </div>

      <div className="pt-4">
        <h3 className="text-lg font-semibold text-gray-800">Líder da Equipe (Você)</h3>
        {lider ? (
          <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg mt-2 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Nome Completo" value={lider.nome_completo || ''} disabled />
              <Input label="CPF" value={formatCPF(lider.cpf || '')} disabled />
              <Input
                label="Data de Nascimento"
                value={lider.data_nascimento ? lider.data_nascimento.split('T')[0] : ''}
                disabled
                type="date"
              />
              <Input label="Telefone" value={formatTelefone(lider.telefone || '')} disabled />
              <Input label="Escola" value={lider.escola || ''} disabled />
              <div>
                <label className="block text-sm font-medium text-gray-700">Série</label>
                <input
                  value={
                    lider.serie === '9_fundamental' ? '9º Ano - Ensino Fundamental' :
                     lider.serie === '8_fundamental' ? '8º Ano - Ensino Fundamental' :
                      lider.serie === '1_medio' ? '1º Ano - Ensino Médio' :
                        lider.serie === '2_medio' ? '2º Ano - Ensino Médio' :
                        lider.serie === '3_medio' ? '3º Ano - Ensino Médio' :
                          lider.serie || ''
                  }
                  disabled
                  className="mt-1 w-full p-2 border border-gray-300 bg-gray-200 rounded-md cursor-not-allowed"
                />
              </div>

              {/* NOVO CAMPO — Categoria do Líder (somente leitura) */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Categoria</label>
                <input
                  value={
                    lider.categoria ||
                    'Não informada'
                  }
                  disabled
                  className="mt-1 w-full p-2 border border-gray-300 bg-gray-200 rounded-md cursor-not-allowed"
                />
              </div>
            </div>

            {/* Aviso de regra (quando aplicável) */}
            {(leaderIsPass || leaderIsExt) && (
              <p className="text-sm text-yellow-700 mt-2">
                <strong>Regra:</strong> como líder cadastrado como <em>{lider.categoria}</em>, você não pode misturar categorias na equipe.
                {leaderIsPass && " — alunos externos serão desabilitados."}
                {leaderIsExt && " — alunos do Passaporte Digital serão desabilitados."}
              </p>
            )}
          </div>
        ) : (
          <p className="text-gray-600">Carregando seus dados...</p>
        )}
      </div>

      <div className="pt-4">
        <h3 className="text-lg font-semibold text-gray-800">Outros Membros da Equipe</h3>

        {fields.map((field, index) => (
          <div key={field.id} className="p-4 border border-gray-200 rounded-lg my-4 space-y-4 relative">
            <h4 className="font-bold text-gray-700">Novo Membro {index + 1}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome Completo"
                error={errors?.novosMembros?.[index]?.nome_completo}
                {...formRegister(`novosMembros.${index}.nome_completo`, { required: "Informe o nome completo." })}
              />
              <Input
                label="CPF"
                maxLength="14"
                onInput={(e) => handleInputChange(e, formatCPF)}
                error={errors?.novosMembros?.[index]?.cpf}
                {...formRegister(`novosMembros.${index}.cpf`, { required: "Informe o CPF." })}
              />
              <Input
                label="Data de Nascimento"
                type="date"
                error={errors?.novosMembros?.[index]?.data_nascimento}
                {...formRegister(`novosMembros.${index}.data_nascimento`, { required: "Informe a data de nascimento." })}
              />
              <Input
                label="Telefone"
                maxLength="15"
                onInput={(e) => handleInputChange(e, formatTelefone)}
                error={errors?.novosMembros?.[index]?.telefone}
                {...formRegister(`novosMembros.${index}.telefone`, { required: "Informe o telefone." })}
              />
              <Input
                label="Escola"
                error={errors?.novosMembros?.[index]?.escola}
                {...formRegister(`novosMembros.${index}.escola`, { required: "Informe a escola." })}
              />

              {/* ===== Campo Série ===== */}
              <div>
                <label htmlFor={`novosMembros.${index}.serie`} className="block text-sm font-medium text-gray-700">Série</label>
                <select
                  id={`novosMembros.${index}.serie`}
                  {...formRegister(`novosMembros.${index}.serie`, { required: "Selecione a série." })}
                  className={`mt-1 w-full p-2 border ${errors?.novosMembros?.[index]?.serie ? 'border-red-500' : 'border-gray-300'
                    } rounded-md`}
                >
                  <option value="">Selecione a série</option>
                  <option value="8_fundamental">8º Ano - Ensino Fundamental</option>
                  <option value="9_fundamental">9º Ano - Ensino Fundamental</option>
                  <option value="1_medio">1º Ano - Ensino Médio</option>
                  <option value="2_medio">2º Ano - Ensino Médio</option>
                  <option value="3_medio">3º Ano - Ensino Médio</option>
                </select>
                {errors?.novosMembros?.[index]?.serie && (
                  <span className="text-red-600 text-sm">
                    {errors.novosMembros[index].serie.message}
                  </span>
                )}
              </div>

              {/* ===== NOVO CAMPO: Categoria ===== */}
              <div>
                <label htmlFor={`novosMembros.${index}.categoria`} className="block text-sm font-medium text-gray-700">
                  Categoria
                </label>
                <select
                  id={`novosMembros.${index}.categoria`}
                  {...formRegister(`novosMembros.${index}.categoria`, { required: "Selecione a categoria." })}
                  className={`mt-1 w-full p-2 border ${errors?.novosMembros?.[index]?.categoria ? 'border-red-500' : 'border-gray-300'
                    } rounded-md`}
                >
                  <option value="">Selecione uma categoria</option>

                  <option
                    value="EX ALUNO DO PASSAPORTE DIGITAL"
                    disabled={leaderIsExt}
                    title={leaderIsExt ? "Líder externo: não é permitido adicionar aluno do Passaporte" : ""}
                  >
                    EX-ALUNO DO PASSAPORTE DIGITAL
                  </option>

                  <option
                    value="ALUNO ATUAL DO PASSAPORTE DIGITAL"
                    disabled={leaderIsExt}
                    title={leaderIsExt ? "Líder externo: não é permitido adicionar aluno do Passaporte" : ""}
                  >
                    ALUNO ATUAL DO PASSAPORTE DIGITAL
                  </option>

                  <option
                    value="ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA"
                    disabled={leaderIsPass}
                    title={leaderIsPass ? "Líder do Passaporte: não é permitido adicionar estudante da rede local (externo)" : ""}
                  >
                    ESTUDANTE DA REDE DE ESCOLAS DO MUNICÍPIO DE LAGOA DE ITAENGA
                  </option>
                </select>
                {errors?.novosMembros?.[index]?.categoria && (
                  <span className="text-red-600 text-sm">
                    {errors.novosMembros[index].categoria.message}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => remove(index)}
              className="absolute top-2 right-2 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
            >
              Remover
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={() =>
            append({
              nome_completo: "",
              cpf: "",
              data_nascimento: "",
              telefone: "",
              escola: "",
              serie: "",
              categoria: "",
            })
          }
          disabled={fields.length >= 3}
          className="mt-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Adicionar Novo Membro
        </button>

        {errors.novosMembros && (errors.novosMembros.root || errors.novosMembros) && (
          <p className="mt-2 text-sm text-red-600 font-medium">
            {errors.novosMembros.root?.message || errors.novosMembros.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Step1_InfoEquipe;
